import { useContext, createContext, FC, useRef, useState } from "react";
import { useDeepCompareEffect } from "use-deep-compare";
import * as _ from "lodash";

import { huePalette, ColorType, localColorStoreKey, Optional } from "@ctra/utils";
import { ChartSeriesType, DataPointsData } from "@ctra/api";

import { useChartContext } from "../ChartContext";

type ContextType = {
  palette: Record<string, ColorType>;
  paletteType: string;
  api: {
    setPaletteType: (type: ChartSeriesType) => void;
    getColor: (cacheKey: Optional<string>, itemKey: Optional<string>) => ColorType;
  };
};

const DefaultContext = createContext<ContextType>({
  palette: {},
  paletteType: "default",
  api: {
    setPaletteType: _.noop,
    getColor: () => ({ hue: 0, saturation: 0, luminosity: 0 })
  }
});

/**
 * Default color palette
 */
const defaultColors = JSON.stringify({
  /**
   * default color for each behavior
   */
  behavior: {
    eatingPct: { hue: 0, saturation: 100, luminosity: 27 },
    ruminationPct: { hue: 96, saturation: 100, luminosity: 27 },
    lyingPct: { hue: 164, saturation: 100, luminosity: 27 },
    standingPct: { hue: 208, saturation: 100, luminosity: 27 },
    restingPct: { hue: 61, saturation: 100, luminosity: 28 },
    otherPct: { hue: 276, saturation: 100, luminosity: 50 }
  }
});

/**
 * Here, we generate color from our color palette (provided by the design team) to be used by a group of items
 *
 * This function takes in a cacheKey and an item key and assigns the next available color to the item
 *
 * At the moment, we can generate 26 unique colors from this.
 * If you ever need to increase this number, you need to play around with the divisor factor
 * and the gap between the saturation and luminosity
 *
 * We have 13 colors in the list from design. To get the 14th color, we do the following:
 * 14 % 13 = 1, then set the hue to palette[1], saturation to 75 (_.floor(14/13)) and luminosity to saturation - 50
 * @param {Record<string, unknown>} cacheObject
 * @param {Array<number>} list
 * @returns {ColorType}
 */
const assignColor = (cacheObject: Record<string, unknown>, list = huePalette): ColorType => {
  const cacheSize = _.size(cacheObject);
  const sizeIndex = cacheSize % list.length;
  const hue = list[sizeIndex];

  /**
   * Numbers ranging from 0 - list.length will produce 0, then 1
   * @todo deal with more rounds than only 2
   */
  const divisorFactor = _.floor(cacheSize / list.length) % 2;

  const saturation = divisorFactor === 0 ? 90 : 50;
  const luminosity = saturation - (divisorFactor === 0 ? 35 : 5);

  return { hue, saturation, luminosity };
};

/**
 * Series color provider
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @return {JSX.Element}
 */
const _SeriesColorProvider: FC = ({ children }) => {
  const colorPalette = useRef<Record<string, Record<string, ColorType>>>(
    JSON.parse(_.defaultTo(localStorage.getItem(localColorStoreKey), defaultColors))
  );

  const {
    values: { meta }
  } = useChartContext<DataPointsData>();

  /**
   * Merge all series types from the main and overlay meta into one array
   * @type {string[]}
   */
  const seriesTypes = _.uniq(_.compact(_.concat(..._.map(meta, "seriesType"))));

  /**
   * Make an API for palette type. This should be accessed from outside so
   * the nuances of overlays vs. no overlays are managed explicitly by the chart.
   */
  const [paletteType, setPaletteType] = useState<string>(_.first(seriesTypes));

  /**
   * Gets the color from the local store
   * @todo allow use of generics
   * @param {Optional<string>} cacheKey
   * @param {Optional<string>} itemKey
   * @returns {ColorType}
   */
  const getColor: ContextType["api"]["getColor"] = (
    cacheKey: Optional<string>,
    itemKey: Optional<string>
  ): ColorType => {
    /**
     * Use the paletteType as the cache key when the cacheKey is not provided
     * @type {string}
     */
    cacheKey = _.defaultTo(cacheKey, paletteType);

    /**
     * Try to pick a color from the cache
     */
    let color = cacheKey && itemKey ? _.get(colorPalette.current, [cacheKey, itemKey], null) : null;

    /**
     * Make a color if it does not exist in the cache yet
     */
    if (cacheKey && itemKey && !color) {
      /**
       * Pick the color list already assigned to the series type, eg. "farm colors"
       * @type {Record<string, ColorType>}
       */
      const cacheObject: Record<string, ColorType> = _.get(colorPalette.current, paletteType, {});

      /**
       * Assign a unique color to the item
       * @type {ColorType}
       */
      color = assignColor(cacheObject);

      /**
       * Update the color palette with the new color
       * @type {{[p: string]: {[p: string]: any}}}
       */
      colorPalette.current = {
        ...colorPalette.current,
        [paletteType]: {
          ...colorPalette.current[paletteType],
          [itemKey]: color
        }
      };
    }

    return _.defaultTo(color, {
      hue: 0,
      saturation: 0,
      luminosity: 245 // use a really light color as a fallback
    });
  };

  /**
   * Save colors to the local storage
   */
  useDeepCompareEffect(() => {
    localStorage.setItem(localColorStoreKey, JSON.stringify(colorPalette.current));
  }, [colorPalette.current]);

  return (
    <DefaultContext.Provider
      value={{
        api: {
          getColor,
          setPaletteType
        },
        palette: colorPalette.current[paletteType],
        paletteType
      }}
    >
      {children}
    </DefaultContext.Provider>
  );
};

export const SeriesColorContext = {
  Provider: _SeriesColorProvider,
  Consumer: DefaultContext.Consumer
};

/**
 * Hook for the series color context
 */
export const useSeriesColor = (): ContextType => useContext(DefaultContext);
